import { z } from "zod";

/**
 * The canonical error model (SPEC §57) and its HTTP mapping.
 *
 * Two rules matter here:
 *
 *   - The `code` is stable and machine-readable. Clients branch on it.
 *   - `message` is safe to show a user. Internal details (SQL, stack traces,
 *     GitHub response bodies) are logged server-side and never serialized
 *     (AGENTS.md §21).
 */

export const ERROR_CODES = [
  // SPEC §57 — canonical set.
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "VALIDATION_ERROR",
  "CONTENT_CONFLICT",
  "GITHUB_ERROR",
  "GITHUB_RATE_LIMIT",
  "UPLOAD_TOO_LARGE",
  "INVALID_MEDIA_TYPE",
  "PUBLISH_FAILED",
  "BUILD_FAILED",
  "INVALID_THEME",
  "INVALID_LOCALE",
  "DISCUSSION_ERROR",
  "LLM_ERROR",
  "INTERNAL_ERROR",

  // Domain-specific codes. AGENTS.md §21 names SLUG_ALREADY_EXISTS explicitly;
  // the rest exist so that callers never have to fall back to INTERNAL_ERROR
  // for an expected outcome.
  "SLUG_ALREADY_EXISTS",
  "ENTRY_ALREADY_EXISTS",
  "LOCALIZATION_ALREADY_EXISTS",
  "UNSUPPORTED_LOCALE",
  "INVALID_PATH",
  "NOT_CONFIGURED",
  "RATE_LIMITED",
  "METHOD_NOT_ALLOWED",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  "CONFLICT",
] as const;

export const errorCodeSchema = z.enum(ERROR_CODES);

export type ErrorCode = z.infer<typeof errorCodeSchema>;

/** Wire format of an API error body (SPEC §57). */
export const apiErrorBodySchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;

const DEFAULT_STATUS: Record<ErrorCode, number> = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
  CONTENT_CONFLICT: 409,
  GITHUB_ERROR: 502,
  GITHUB_RATE_LIMIT: 429,
  UPLOAD_TOO_LARGE: 413,
  INVALID_MEDIA_TYPE: 415,
  PUBLISH_FAILED: 502,
  BUILD_FAILED: 502,
  INVALID_THEME: 422,
  INVALID_LOCALE: 422,
  DISCUSSION_ERROR: 502,
  LLM_ERROR: 502,
  INTERNAL_ERROR: 500,

  SLUG_ALREADY_EXISTS: 409,
  ENTRY_ALREADY_EXISTS: 409,
  LOCALIZATION_ALREADY_EXISTS: 409,
  UNSUPPORTED_LOCALE: 422,
  INVALID_PATH: 400,
  NOT_CONFIGURED: 503,
  RATE_LIMITED: 429,
  METHOD_NOT_ALLOWED: 405,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  CONFLICT: 409,
};

/**
 * An error that is safe to serialize to an API client.
 *
 * `cause` carries the underlying failure for server-side logging. It is never
 * included in `toBody()`.
 */
export class AppError extends Error {
  override readonly name = "AppError";

  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ErrorCode,
    message: string,
    options: { details?: Record<string, unknown>; status?: number; cause?: unknown } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.code = code;
    this.status = options.status ?? DEFAULT_STATUS[code];
    this.details = options.details;
  }

  /** Safe wire representation. */
  toBody(): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details === undefined ? {} : { details: this.details }),
      },
    };
  }
}

/** True when `value` is an `AppError` (survives cross-realm `instanceof` pitfalls). */
export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError || (value instanceof Error && value.name === "AppError");
}

/**
 * Convert any thrown value into an `AppError`.
 *
 * Unknown failures collapse to `INTERNAL_ERROR` with a generic message so that
 * a SQLite constraint string or a GitHub stack trace can never reach a client.
 */
export function toAppError(value: unknown): AppError {
  if (isAppError(value)) {
    return value;
  }

  return new AppError("INTERNAL_ERROR", "An unexpected error occurred.", { cause: value });
}

// ---------------------------------------------------------------------------
// Convenience constructors for the failures that come up constantly.
// ---------------------------------------------------------------------------

export const errors = {
  unauthorized: (message = "Authentication is required.") => new AppError("UNAUTHORIZED", message),

  forbidden: (message = "You do not have access to this resource.") =>
    new AppError("FORBIDDEN", message),

  notFound: (what: string, id?: string) =>
    new AppError("NOT_FOUND", id ? `${what} "${id}" was not found.` : `${what} was not found.`),

  validation: (message: string, details?: Record<string, unknown>) =>
    new AppError("VALIDATION_ERROR", message, details ? { details } : {}),

  invalidLocale: (locale: string, supported: readonly string[] = []) =>
    new AppError("INVALID_LOCALE", `"${locale}" is not a valid locale.`, {
      details: supported.length > 0 ? { supported: [...supported] } : {},
    }),

  unsupportedLocale: (locale: string, supported: readonly string[]) =>
    new AppError("UNSUPPORTED_LOCALE", `Locale "${locale}" is not enabled for this site.`, {
      details: { supported: [...supported] },
    }),

  slugTaken: (slug?: string) =>
    new AppError("SLUG_ALREADY_EXISTS", "This slug is already used by another article.", {
      ...(slug === undefined ? {} : { details: { slug } }),
    }),

  conflict: (
    message = "The content repository has changed since this draft was loaded. Review the GitHub changes before publishing.",
  ) => new AppError("CONTENT_CONFLICT", message),

  invalidPath: (path: string) =>
    new AppError("INVALID_PATH", "The provided path is not allowed.", { details: { path } }),

  uploadTooLarge: (size: number, max: number) =>
    new AppError("UPLOAD_TOO_LARGE", `Files must be ${String(max)} bytes or smaller.`, {
      details: { size, max },
    }),

  invalidMediaType: (mimeType: string, allowed: readonly string[]) =>
    new AppError("INVALID_MEDIA_TYPE", `Media type "${mimeType}" is not supported.`, {
      details: { allowed: [...allowed] },
    }),

  notConfigured: (what: string) =>
    new AppError("NOT_CONFIGURED", `${what} is not configured on this deployment.`),

  /**
   * The caller has exceeded a rate limit (TASK-902).
   *
   * `retryAfterSeconds` goes in `details` because the client's correct response
   * is to wait that long — and a 429 without it invites the exact behaviour
   * that caused the limit to be hit.
   */
  rateLimited: (what = "This operation", retryAfterSeconds?: number) =>
    new AppError(
      "RATE_LIMITED",
      `${what} is being rate limited. Try again in a moment.`,
      retryAfterSeconds === undefined ? {} : { details: { retryAfterSeconds } },
    ),

  github: (message: string, details?: Record<string, unknown>) =>
    new AppError("GITHUB_ERROR", message, details ? { details } : {}),

  githubRateLimit: (resetAt?: string) =>
    new AppError("GITHUB_RATE_LIMIT", "The GitHub API rate limit has been reached.", {
      details: resetAt ? { resetAt } : {},
    }),

  /** A configured LLM endpoint failed or answered unusably (SPEC §74). */
  llm: (message: string, details?: Record<string, unknown>) =>
    new AppError("LLM_ERROR", message, details ? { details } : {}),

  internal: (message = "An unexpected error occurred.", cause?: unknown) =>
    new AppError("INTERNAL_ERROR", message, cause === undefined ? {} : { cause }),
} as const;
